// Canonical Help Genie mascot constants — single source of truth for the
// new mascot library at /public/genie-mascots/. Components import from here
// instead of hardcoding paths so future swaps are one-line changes.
//
// Migration context: the OLD asset set at /public/genie-images/ (numbered
// 1-13, "Help Genie Mascot Large.png", "HG Genies.png") is being retired.
// Files stay on disk until a render-audit confirms zero 404s. Until then,
// resolveLegacyGeniePath() maps any persisted old path (e.g. a customer's
// stored customAvatarUrl) to the closest new mascot.

import { roleMascots, type RoleSlug } from "./genieMascotsRole";

// The portrait/round mascot used everywhere a "the Help Genie" face is shown:
// chat empty states, sidebar header, channel splash, onboarding checklist,
// brain widget, smart-prompts mascot. Matches the new mascot family.
export const BRAND_MASCOT_LARGE = "/genie-mascots/role/support.png";

// Same image, used wherever the smaller chrome mascot was previously
// "Help Genie Mascot.png" (sidebar collapsed, MyGenies header avatar).
export const BRAND_MASCOT = "/genie-mascots/role/support.png";

// Replacement for the legacy "HG Genies.png" group/concierge composition.
export const BRAND_MASCOT_CONCIERGE = "/genie-mascots/role/concierge.png";

// Universal onError fallback. If any mascot src 404s during migration,
// MascotImg swaps to this so no broken-image icon ever ships.
export const MASCOT_FALLBACK = "/genie-mascots/role/support.png";

// Curated 13-slot pool that replaces the legacy /genie-images/1.png..13.png
// random/seeded selector. Order preserved so getSeededGenieDefaultImage
// produces stable results for the same input seed.
const POOL_SLUGS: RoleSlug[] = [
  "support",
  "concierge",
  "receptionist",
  "booking",
  "lead-capture",
  "quote",
  "survey",
  "audit",
  "feedback",
  "onboarding",
  "marketing",
  "sales",
  "knowledge",
];

export const BRAND_MASCOT_POOL: string[] = POOL_SLUGS.map((slug) => roleMascots[slug]);

// 1-to-1 map from legacy numbered paths to the new pool. Any persisted
// customAvatarUrl like "/genie-images/3.png" routes here instead of 404ing.
const LEGACY_NUMBERED_MAP: Record<string, string> = Object.fromEntries(
  POOL_SLUGS.map((slug, index) => [`/genie-images/${index + 1}.png`, roleMascots[slug]])
);

// Named legacy mascot paths (the brand head shot + the group composition).
const LEGACY_NAMED_MAP: Record<string, string> = {
  "/genie-images/Help Genie Mascot.png": BRAND_MASCOT,
  "/genie-images/Help Genie Mascot Large.png": BRAND_MASCOT_LARGE,
  "/genie-images/HG Genies.png": BRAND_MASCOT_CONCIERGE,
  "/optimized/genie-images/HG Genies.webp": BRAND_MASCOT_CONCIERGE,
  "/optimized/genie-images/HG Genies-mobile.webp": BRAND_MASCOT_CONCIERGE,
  "/optimized/genie-images/HG Genies.png": BRAND_MASCOT_CONCIERGE,
};

export const LEGACY_GENIE_IMAGE_MAP: Record<string, string> = {
  ...LEGACY_NUMBERED_MAP,
  ...LEGACY_NAMED_MAP,
};

/**
 * Translate a legacy /genie-images/ or /optimized/genie-images/ path to the
 * matching new /genie-mascots/ path. Non-legacy paths pass through unchanged
 * so DB-stored URLs to S3/Supabase/CDN are never rewritten.
 */
export const resolveLegacyGeniePath = (src: string | null | undefined): string | undefined => {
  if (!src) return undefined;
  return LEGACY_GENIE_IMAGE_MAP[src] ?? src;
};
